import { history, Nullable } from "@data-force/app";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { TranslateFunction } from "react-localize-redux";
import { match, matchPath } from "react-router";
import {
    JobTag, LocalePrefix, PhaseStatus, ProjectType,
} from "../enums/enums";
import { Editors } from "../interfaces/common";
import HistoryParams from "../interfaces/historyParams";
import { JobEntity, PhaseConfigNormalized } from "../interfaces/store/actions/payload/job";
import { TaskPhaseStats } from "../interfaces/store/actions/payload/task";
import { CT_WEB_EDITOR } from "./constants";

dayjs.extend(duration);

/**
 * Regex for searching placeholders in string template
 */
const formatStringRegex: RegExp = /{(\d+)}/g;

/**
 * Format string with values and template
 * @param {string} template
 * @param {string[]} values
 * @return {string}
 */
const formatString = (template: string, ...values: string[]): string => template.replace(
    formatStringRegex, (test: string, index: number): string => (typeof values[index] !== "undefined" ? values[index] : test),
);

const getHistoryParams = (): Nullable<HistoryParams> => {
    const matchResult: Nullable<match<HistoryParams>> = matchPath(history.location.pathname, "/:idpId/:view?/:projectId?");
    return matchResult?.params;
};

/**
 * Calculates status of job in phase for which stats are passed
 * @param {JobEntity} data
 * @return {string} task status
 */
function getJobPhaseStatus(data: JobEntity): string {
    const {
        phaseStats, completedTaskCount, assignee, tags, status,
    }: JobEntity = data;
    let jobPhaseStatus: string = PhaseStatus.IN_PROGRESS;
    if (phaseStats) {
        const {
            FAILED = 0,
            NOT_STARTED = 0,
            READY = 0,
            REJECTED = 0,
            REOPENED = 0,
            SKIPPED = 0,
            IN_PROGRESS = 0,
            COMPLETED = 0,
        }: TaskPhaseStats = phaseStats;
        // Tasks count is sum of all except skipped because that status should be excluded from job status calculation
        const taskCount: number = COMPLETED + FAILED + IN_PROGRESS + NOT_STARTED + READY + REJECTED + REOPENED;
        if (taskCount) {
            if (tags.includes(JobTag.EXPIRED) && status !== PhaseStatus.COMPLETED) {
                // when job tags contain expired and phase is not completed, we mark the phase as expired
                jobPhaseStatus = PhaseStatus.EXPIRED;
            } else if (taskCount === FAILED) {
                jobPhaseStatus = PhaseStatus.FAILED;
            } else if (taskCount === completedTaskCount && COMPLETED) {
                // completedTaskCount represents total of rejected and completed tasks,
                // hence, if that number is equal to taskCount and at least one of completed tasks is in completed status
                // we mark whole phase as completed
                jobPhaseStatus = PhaseStatus.COMPLETED;
            } else if (taskCount === REJECTED) {
                // if number of task count is equal to number of rejected ones, we mark the phase as rejected
                jobPhaseStatus = PhaseStatus.REJECTED;
            } else if (taskCount === REOPENED) {
                // if number of all tasks is equal to number of rejected ones, we mark the phase as reopened
                jobPhaseStatus = PhaseStatus.REOPENED;
            } else if (taskCount === NOT_STARTED) {
                // When job hasn't been claimed or assigned he wont have assignee value and should be marked as new.
                // If phase has no assignee value it should be marked as new
                jobPhaseStatus = assignee ? PhaseStatus.NOT_STARTED : PhaseStatus.NEW;
            } else if (taskCount === READY || taskCount === (READY + NOT_STARTED)) {
                // When job hasn't been claimed or assigned he wont have assignee value and should be marked as new.
                // Phase that has at least one task ready to be processed should be marked as ready
                jobPhaseStatus = assignee ? PhaseStatus.READY : PhaseStatus.NEW;
            }
        } else if (SKIPPED) {
            // if task count is zero and there is skipped ones we mark whole phase as rejected
            jobPhaseStatus = PhaseStatus.REJECTED;
        }
    }

    return jobPhaseStatus.toLowerCase();
}

/**
 * Check if CT WEB editor should be used for a phase
 * @param {PhaseConfigNormalized} phaseConfigList
 * @param {JobEntity} data
 * @return boolean
 */
function isCtWebJob(
    phaseConfigList: PhaseConfigNormalized,
    phase: string,
    projectType: string,
): boolean {
    return CT_WEB_EDITOR === phaseConfigList[`${projectType}-${phase}`]?.editor;
}

/**
 * Get opening url for editor
 * @param {Editors} editors
 * @param {PhaseConfigNormalized} phaseConfigList
 * @param {JobEntity} data
 * @return string
 */
function getOpeningUrl(editors: Editors, phaseConfigList: PhaseConfigNormalized, data: JobEntity): string {
    let openingUrl: string = "";
    if (data) {
        const {
            name: phase, projectId, jobId, id: jobPhaseId, projectType, phaseStats,
        }: JobEntity = data;
        const editor: string = phaseConfigList[`${projectType}-${phase}`]?.editor;

        if (editor && editor !== "mobile" && phaseStats) {
            // Jobs with no tasks should not be opened in editor
            const editorUrl: string = editors[editor];
            const historyParams: Nullable<HistoryParams> = getHistoryParams();
            openingUrl = `${editorUrl}/#/${historyParams?.idpId}/${phase}/${projectId}/${jobId}/${jobPhaseId}`;
        }
    }

    return openingUrl;
}

/**
 * Returns formatted estimated time,
 * if estimated time is under 1hour we return X-minutes,
 * if there is full hours we return X-hours format,
 * if there are hours with minutes, we return X-hours Y-minutes format
 * @param {number} time
 * @param {TranslateFunction} translate
 * @return {string}
 */
function getFormattedTime(time: number, translate: TranslateFunction): string {
    const estimatedTime: number = dayjs.duration(time * 1000).asMinutes();
    const hours:number = Math.floor(estimatedTime / 60);
    const minutes:number = estimatedTime % 60;
    let formattedTime: string;
    const minutesFormat: string = translate("minutesFormat.text") as string;
    const hoursFormat: string = translate("hoursFormat.text") as string;
    const hoursMinutesFormat: string = translate("hoursMinutesFormat.text") as string;

    if (estimatedTime < 60) {
        formattedTime = formatString(minutesFormat, estimatedTime.toString());
    } else if (minutes) {
        formattedTime = formatString(hoursMinutesFormat, hours.toString(), minutes.toString());
    } else {
        formattedTime = formatString(hoursFormat, hours.toString());
    }

    return formattedTime;
}

/**
 * Depending on job type, we return locale key for jobs/tests
 * @param {Nullable<boolean>} isTest
 * @return {string}
 */
const getLocalePrefix = (isTest: Nullable<boolean>): string => (isTest ? LocalePrefix.TEST : LocalePrefix.JOB);

/**
 * Checks if the given projectType is an external project and returns a boolean value
 *
 * @param {Nullable<string>} projectType
 * @return {boolean}
 */
const isExternalProject = (projectType: Nullable<string>): boolean => projectType === ProjectType.EXTERNAL;

/**
 * Based on document query for custom attr 'df-current' we find if there are any elements that need to be focused.
 * When element is focused, we remove the custom attr from the element, allowing for the next element to be focused.
 */
const setFocusedElement = (): void => {
    const currentEl: HTMLElement = document.querySelector("[data-focus='df-current']") as HTMLElement;
    if (currentEl) {
        currentEl.focus();
        currentEl.removeAttribute("data-focus");
    }
};

export {
    formatString,
    getFormattedTime,
    getHistoryParams,
    getJobPhaseStatus,
    getLocalePrefix,
    getOpeningUrl,
    isCtWebJob,
    isExternalProject,
    setFocusedElement,
};
